import { getEntityList } from '@/composables/globalData/useEntitiesData'
import { ENTITIES_CONFIG } from '@/enum/etitiesConfig'

const normalize = (str) => String(str).toLowerCase().replace(/[^a-z0-9]/g, '')

const findEntityConfig = (idKey) => {
    if (!idKey) return null
    for (const [key, config] of Object.entries(ENTITIES_CONFIG)) {
        if (normalize(config.idKey) === normalize(idKey)) return { entityKey: key, config }
    }
    return null
}

// Create both mappings in one pass - more efficient even if only one is used
const createEntityMaps = (entityKey, { idKey, labelKey }) => {
    const entities = getEntityList(entityKey)
    if (!Array.isArray(entities) || !entities.length) {
        return { labelToId: new Map(), idToLabel: new Map() }
    }
    
    const labelToId = new Map()
    const idToLabel = new Map()
    
    entities.forEach(e => {
        if (e[idKey] != null && e[labelKey] != null) {
            const normalizedLabel = normalize(e[labelKey])
            labelToId.set(normalizedLabel, e[idKey])
            idToLabel.set(String(e[idKey]), normalizedLabel)
        }
    })
    
    return { labelToId, idToLabel }
}

const findFieldByNormalized = (row, targetKey) => {
    if (!row || !targetKey) return null
    const fieldMap = new Map()
    for (const key of Object.keys(row)) {
        const norm = normalize(key)
        if (!fieldMap.has(norm)) fieldMap.set(norm, key)
    }
    return fieldMap.get(normalize(targetKey)) || null
}

/** For keys like "user_id", returns "user" (the part without "id"); otherwise null. */
function getDerivedLabelPart(idKey) {
    if (typeof idKey !== 'string' || !idKey.includes('_')) return null
    const parts = idKey.split('_').filter(Boolean)
    if (!parts.some(p => normalize(p) === 'id')) return null
    return parts.filter(p => normalize(p) !== 'id').join('_') || null
}

const findIdFieldByMatching = (tableData, defaultList, cachedIdKey) => {
    if (!tableData?.length || !defaultList?.length || !cachedIdKey) return null
    
    const entityInfo = findEntityConfig(cachedIdKey)
    if (!entityInfo) return null
    
    const { entityKey, config } = entityInfo
    let labelField = findFieldByNormalized(tableData[0], config.labelKey)
    if (!labelField) {
        const labelPart = getDerivedLabelPart(cachedIdKey)
        if (labelPart) labelField = findFieldByNormalized(tableData[0], labelPart)
    }
    if (!labelField) return null
    
    const { labelToId } = createEntityMaps(entityKey, config)
    if (!labelToId.size) return null
    
    const defaultSet = new Set(defaultList.map(String))
    let matches = 0, total = 0
    
    for (const row of tableData) {
        const label = row[labelField]
        if (label != null) {
            total++
            const entityId = labelToId.get(normalize(label))
            if (entityId != null && defaultSet.has(String(entityId))) matches++
        }
    }
    
    return matches > 0 && matches / total >= 0.5 ? labelField : null
}

export function getEntityIdField(tableData, cachedIdKey = null, defaultList = null) {
    if (!tableData?.length) return null
    
    const idField = findFieldByNormalized(tableData[0], cachedIdKey)
    if (idField) return idField
    
    return findIdFieldByMatching(tableData, defaultList, cachedIdKey)
}

const idFieldCache = new Map()

export function sortTableDataByDefaultList(tableData, widgetData, idField = null) {
    if (!tableData?.length) return tableData

    const { defaultList, defaultProp: cachedIdKey, order } = widgetData?.WidgetLayout?.sortingParams || {}
    if (!defaultList?.length || order) return tableData

    idField = idField || idFieldCache.get(cachedIdKey) || getEntityIdField(tableData, cachedIdKey, defaultList)
    
    if (!idField) return tableData
    if (!idFieldCache.has(cachedIdKey)) idFieldCache.set(cachedIdKey, idField)

    // Local orderMap for this invocation only (no global accumulation)
    let orderMap = new Map(defaultList.map((id, i) => [String(id), i]))

    const entityInfo = findEntityConfig(cachedIdKey)
    const labelPart = getDerivedLabelPart(cachedIdKey)
    const isLabelColumn = entityInfo && normalize(idField) === normalize(entityInfo.config.labelKey)
    const isDerivedLabelColumn = entityInfo && labelPart && normalize(idField) === normalize(labelPart)
    if (entityInfo && (isLabelColumn || isDerivedLabelColumn)) {
        const { idToLabel } = createEntityMaps(entityInfo.entityKey, entityInfo.config)
        if (idToLabel.size) {
            orderMap = new Map(
                defaultList.map((id, i) => {
                    const label = idToLabel.get(String(id))
                    return label ? [label, i] : null
                }).filter(Boolean)
            )
        }
    }
    
    return [...tableData].sort((a, b) => {
        const aVal = normalize(a[idField] ?? '')
        const bVal = normalize(b[idField] ?? '')
        return (orderMap.get(aVal) ?? Infinity) - (orderMap.get(bVal) ?? Infinity)
    })
}